/* eslint-disable no-nested-ternary */
import { useQuery } from "@apollo/client";
import { CoinsIcon, HandCoinsIcon, HandshakeIcon } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import ClipLoader from "react-spinners/ClipLoader";

import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import classNames from "components/ui/classNames";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";

import { palette } from "lib/miolo";
import AmericanIssuerDividendsQuery from "lib/queries/AmericanIssuerDividendsQuery";
import type {
  IAmericanIssuerDividendsQuery,
  IAmericanIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/AmericanIssuerDividendsQuery.generated";
import { ISecurityCountry } from "types/graphqlTypes";

import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import AmericanDividendsPaidTabContet from "../content/AmericanDividendsPaidTabContet";
import AmericanSpecialDividendsPaidTabContet from "../content/AmericanSpecialDividendsPaidTabContet";
import AmericanStockDividendsPaidTabContet from "../content/AmericanStockDividendsPaidTabContet";
import AmericanDividendsPaidSummary from "../summary/AmericanDividendsPaidSummary";
import StockDividendSummary from "../summary/StockDividendSummary";

enum DividendsTab {
  Dividends = "DIVIDENDS",
  SpecialDividends = "SPECIAL_DIVIDENDS",
  StockDividends = "STOCK_DIVIDENDS",
}

interface IAmericanIssuerDividendsCollapsibleProps {
  tickerSymbol: string;
  isExpanded: boolean;
  onToggleCollapsed: () => void;
}

export default function AmericanIssuerDividendsCollapsible({
  tickerSymbol,
  isExpanded,
  onToggleCollapsed,
}: IAmericanIssuerDividendsCollapsibleProps) {
  const intl = useIntl();
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);

  const { data, previousData, loading } = useQuery<
    IAmericanIssuerDividendsQuery,
    IAmericanIssuerDividendsQueryVariables
  >(AmericanIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Us,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  if (!data && !previousData && loading) {
    return (
      <div className="flex w-full items-center justify-center">
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  const americanDividends = data ?? previousData;

  if (!americanDividends) {
    return null;
  }

  const { dividendsPaid, americanIssuerSpecialDividendsPaid, stockDividendsPaid } = americanDividends;

  const hasDividends = dividendsPaid.nodes.length > 0;
  const hasSpecialDividends = americanIssuerSpecialDividendsPaid.length > 0;
  const hasStockDividends = stockDividendsPaid.length > 0;

  const dividendsTabs = [
    {
      value: DividendsTab.Dividends,
      title: intl.formatMessage({
        defaultMessage: "Dividendos",
        id: "A7Fc1J",
      }),
      icon: <HandCoinsIcon size={16} strokeWidth={2} />,
      content: (
        <AmericanDividendsPaidTabContet
          data={dividendsPaid}
          loading={loading}
          filterDividendsByYear={filterDividendsByYear}
          setFilterDividendsByYear={(year) => setFilterDividendsByYear(year)}
        />
      ),
      isHidden: !hasDividends,
    },
    {
      value: DividendsTab.SpecialDividends,
      title: intl.formatMessage({
        defaultMessage: "Dividendos Especiais",
        id: "SxM37J",
      }),
      icon: <CoinsIcon size={16} strokeWidth={2} />,
      content: <AmericanSpecialDividendsPaidTabContet data={americanIssuerSpecialDividendsPaid} loading={loading} />,
      isHidden: !hasSpecialDividends,
    },
    {
      value: DividendsTab.StockDividends,
      title: intl.formatMessage({
        defaultMessage: "Bonificação",
        id: "UjC0PD",
      }),
      icon: <HandshakeIcon size={16} strokeWidth={2} />,
      content: <AmericanStockDividendsPaidTabContet data={stockDividendsPaid} loading={loading} />,
      isHidden: !hasStockDividends,
    },
  ];

  if (hasDividends || hasSpecialDividends || hasStockDividends) {
    return (
      <CollapsibleContentSection
        title="Dividends"
        isExpanded={isExpanded}
        onToggleCollapsed={onToggleCollapsed}
        primaryColor={palette.dixie}
        renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Company Financials", "Earnings"]} />}
        renderContentSummary={() => {
          if (hasDividends) {
            return (
              <AmericanDividendsPaidSummary
                tickerSymbol={tickerSymbol}
                dividendsPaidByYear={dividendsPaid.dividendsPaidByYear ?? []}
                dividendYield={dividendsPaid.dividendYieldByTickerSymbol}
              />
            );
          }
          if (hasStockDividends) {
            const [lastStockDividend] = stockDividendsPaid;

            return <StockDividendSummary sharePaid={lastStockDividend.sharePaid} />;
          }

          return null;
        }}
        renderContent={() => (
          <div
            className={classNames(
              "relative rounded-xl border bg-white",
              "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
              "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
              "dark:border-transparent dark:bg-slate-900",
            )}
          >
            <Tabs
              className="w-full space-y-0 rounded-xl font-normal"
              defaultValue={
                hasDividends
                  ? DividendsTab.Dividends
                  : hasSpecialDividends
                    ? DividendsTab.SpecialDividends
                    : DividendsTab.StockDividends
              }
            >
              <TabsList
                className={classNames(
                  "flex w-full flex-1 divide-x overflow-hidden rounded-b-none rounded-t-xl p-0",
                  "bg-slate-50",
                  "dark:divide-slate-950 dark:border-slate-900 dark:bg-slate-800",
                )}
              >
                {dividendsTabs
                  .filter((tab) => !tab.isHidden)
                  .map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className={classNames(
                        "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
                        "border-b-slate-200 dark:border-b-slate-950",
                        "group",
                      )}
                    >
                      <div className="flex flex-col items-center gap-2 truncate py-1 text-left md:flex-row lg:px-1.5">
                        <div
                          className={classNames(
                            "flex items-center justify-center rounded-full p-2 squircle",
                            "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                            "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                            "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                          )}
                        >
                          {tab.icon}
                        </div>
                        <span
                          className={classNames(
                            "text-xs leading-6 lg:text-base",
                            "text-slate-500 group-data-[state=active]:font-bold",
                            "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                          )}
                        >
                          {tab.title}
                        </span>
                      </div>
                    </TabsTrigger>
                  ))}
              </TabsList>

              {dividendsTabs.map((tab) => (
                <TabsContent key={tab.value} value={tab.value} className="border-none p-4 lg:px-6">
                  {tab.content}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}
      />
    );
  }

  return null;
}
